import * as React from 'react'
import type { ReactNode } from 'react'
import { ArrowLeft, Trash2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { PhoneInput } from './PhoneInput'
import {
  DEFAULT_SMART_FIRST_MESSAGE,
  defaultInstructionsFor,
  hasPromptDefault,
  SMART_MAX_DURATION_SECONDS_MAX,
  SMART_MAX_DURATION_SECONDS_MIN,
  SMART_SILENCE_TIMEOUT_SECONDS_MAX,
  SMART_SILENCE_TIMEOUT_SECONDS_MIN,
  type DraftDestination,
} from './transferDraft'
import type {
  DutyHandoffType,
  SmartTransferContextEngineering,
  SmartTransferFirstMessageMode,
} from '@/features/tools/types'
import { normalizePhoneToE164 } from '@/utils/phone'

const FIRST_MESSAGE_MODE_OPTIONS: {
  value: SmartTransferFirstMessageMode
  label: string
}[] = [
  { value: 'assistant-speaks-first', label: 'Speaks first' },
  { value: 'assistant-waits-for-user', label: 'Waits for user' },
  {
    value: 'assistant-speaks-first-with-model-generated-message',
    label: 'Speaks first (model-generated message)',
  },
]

const CONTEXT_ENGINEERING_OPTIONS: {
  value: SmartTransferContextEngineering
  label: string
}[] = [
  { value: 'all', label: 'Include prior conversation' },
  { value: 'none', label: "Don't include prior conversation" },
]

export type TransferDestinationEditorProps = {
  mode: 'add' | 'edit'
  initial: DraftDestination
  isSaving: boolean
  isArchived: boolean
  onBack: () => void
  onSave: (form: DraftDestination) => void
  onDelete?: () => void
  settingsOwner?: 'organization' | 'duty'
}

function Field({
  label,
  children,
}: {
  label: string
  children: (id: string) => ReactNode
}) {
  const id = React.useId()
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      {children(id)}
    </div>
  )
}

const toggleItemClass =
  'h-8 px-3 text-sm font-medium text-primary-high-contrast data-[state=off]:bg-neutral-ui-bg data-[state=off]:opacity-50'

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

export function TransferDestinationEditor({
  mode,
  initial,
  isSaving,
  isArchived,
  onBack,
  onSave,
  onDelete,
  settingsOwner = 'duty',
}: TransferDestinationEditorProps) {
  const [draft, setDraft] = React.useState<DraftDestination>(initial)
  const [phoneError, setPhoneError] = React.useState(false)
  const [extensionError, setExtensionError] = React.useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false)
  const fallbackEndCallId = React.useId()
  // Text mirrors of the numeric fields so the input can hold a transient
  // empty/invalid string while the user is typing/clearing it, without ever
  // writing an out-of-range or NaN (from Number('')) value into the draft.
  const [maxDurationText, setMaxDurationText] = React.useState(() =>
    String(initial.maxDurationSeconds)
  )
  const [silenceTimeoutText, setSilenceTimeoutText] = React.useState(() =>
    String(initial.silenceTimeoutSeconds)
  )

  // Not keyof DraftDestination: that would let setField('raw', ...) type-check.
  const setField = (
    field:
      | 'number'
      | 'extension'
      | 'message'
      | 'description'
      | 'instructions'
      | 'firstMessage'
      | 'fallbackMessage',
    value: string
  ) => {
    if (field === 'number') setPhoneError(false)
    if (field === 'extension') setExtensionError(false)
    setDraft((prev) => ({ ...prev, [field]: value }))
  }

  const setNumberField = (
    field: 'maxDurationSeconds' | 'silenceTimeoutSeconds',
    value: number
  ) => {
    setDraft((prev) => ({ ...prev, [field]: value }))
  }

  // Type lives on the draft (single source of truth). Warm and Smart share one
  // Destination instructions box whose contents are saved into the selected
  // type's field (Warm summary message or Smart assistant prompt), so switching
  // to either never carries the box's text across; carrying it would save one
  // type's prompt as the other's. Switching back to the destination's saved
  // type restores its saved prompt; any other switch to Warm or Smart seeds
  // that type's default. Cold has no instructions box, so its draft
  // instructions are left untouched. Switching to Smart also seeds the default
  // firstMessage when the draft has none. Event-handler driven, no effect.
  const instructionsFor = (next: DutyHandoffType, current: string): string => {
    if (!hasPromptDefault(next)) return current
    if (next === initial.handoffType && initial.instructions.trim()) {
      return initial.instructions
    }
    return defaultInstructionsFor(next)
  }

  const changeType = (next: DutyHandoffType) => {
    setDraft((prev) => ({
      ...prev,
      handoffType: next,
      instructions: instructionsFor(next, prev.instructions),
      firstMessage:
        next === 'smart' && !prev.firstMessage.trim()
          ? DEFAULT_SMART_FIRST_MESSAGE
          : prev.firstMessage,
    }))
  }

  // TODO(routing PR 6 follow-up): register this editor with
  // `useRegisterTabEditor` (hooks/useRegisterTabEditor) so the
  // close dialog can save it. Pattern: FastFirstMessageCard.
  const isDirty =
    draft.number !== initial.number ||
    draft.extension !== initial.extension ||
    draft.message !== initial.message ||
    draft.description !== initial.description ||
    draft.instructions !== initial.instructions ||
    draft.firstMessage !== initial.firstMessage ||
    draft.firstMessageMode !== initial.firstMessageMode ||
    draft.maxDurationSeconds !== initial.maxDurationSeconds ||
    draft.silenceTimeoutSeconds !== initial.silenceTimeoutSeconds ||
    draft.fallbackMessage !== initial.fallbackMessage ||
    draft.fallbackEndCallEnabled !== initial.fallbackEndCallEnabled ||
    draft.contextEngineering !== initial.contextEngineering ||
    draft.handoffType !== initial.handoffType

  const handleSave = () => {
    const extension = draft.extension.trim()
    if (extension && !/^[0-9]{1,10}$/.test(extension)) {
      setExtensionError(true)
      toast.error('Extension must be 1–10 digits')
      return
    }
    const durationValues = [maxDurationText, silenceTimeoutText]
    if (
      draft.handoffType === 'smart' &&
      durationValues.some(
        (value) =>
          value.trim().length > 0 && !Number.isInteger(Number(value.trim()))
      )
    ) {
      toast.error('Transfer durations must use whole seconds')
      return
    }
    // Reject invalid numbers here so the save can't silently no-op downstream.
    if (!normalizePhoneToE164(draft.number)) {
      setPhoneError(true)
      toast.error('Please enter a valid phone number')
      return
    }
    onSave(draft)
  }

  return (
    <div
      className={
        settingsOwner === 'organization'
          ? 'flex min-h-0 flex-1 flex-col'
          : 'flex flex-col gap-4'
      }>
      <div
        className={
          settingsOwner === 'organization'
            ? 'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4'
            : 'flex flex-col gap-4'
        }>
        <div className="flex h-8 w-full items-center gap-1 pl-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Back to destinations"
            onClick={onBack}>
            <ArrowLeft className="size-4.5" />
          </Button>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-primary-high-contrast">
            {mode === 'edit' ? 'Edit destination' : 'Add destination'}
          </span>
          {mode === 'edit' && !isArchived && (
            <AlertDialog
              open={confirmDeleteOpen}
              onOpenChange={(open) => {
                // Lock the dialog open while the delete mutation is in flight.
                if (isSaving) return
                setConfirmDeleteOpen(open)
              }}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Delete destination"
                  disabled={isSaving}>
                  <Trash2 className="size-4.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent
                onEscapeKeyDown={(e) => {
                  if (isSaving) e.preventDefault()
                }}>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete destination?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This removes the destination from the {settingsOwner}'s
                    transfer settings. This can't be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isSaving}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    variant="destructive"
                    disabled={isSaving}
                    onClick={(e) => {
                      // Keep the dialog open until the mutation resolves; the
                      // parent navigates away (unmounting it) once it's done.
                      e.preventDefault()
                      onDelete?.()
                    }}>
                    {isSaving ? (
                      <>
                        <Loader2 className="animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      'Delete'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-primary-high-contrast">
              Transfer type
            </span>
            <ToggleGroup
              type="single"
              aria-label="Transfer type"
              value={draft.handoffType}
              onValueChange={(v) => v && changeType(v as DutyHandoffType)}
              disabled={isArchived}>
              <ToggleGroupItem
                value="cold"
                className={cn(
                  toggleItemClass,
                  'rounded-l-lg rounded-r-none data-[state=on]:bg-primary-subtle-bg data-[state=on]:text-primary-solid'
                )}>
                Cold
              </ToggleGroupItem>
              <ToggleGroupItem
                value="warm"
                className={cn(
                  toggleItemClass,
                  'rounded-none data-[state=on]:bg-hanna-ui-bg data-[state=on]:text-hanna-solid'
                )}>
                Warm
              </ToggleGroupItem>
              <ToggleGroupItem
                value="smart"
                className={cn(
                  toggleItemClass,
                  'rounded-l-none rounded-r-lg data-[state=on]:bg-success-ui-bg data-[state=on]:text-success-solid'
                )}>
                Smart
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <Field label="Phone number">
            {(id) => (
              <PhoneInput
                id={id}
                value={draft.number}
                onChange={(n) => setField('number', n)}
                hasError={phoneError}
                disabled={isArchived}
              />
            )}
          </Field>
          <Field label="Extension (optional)">
            {(id) => (
              <Input
                id={id}
                value={draft.extension}
                onChange={(e) => setField('extension', e.target.value)}
                placeholder="Enter extension"
                inputMode="numeric"
                aria-invalid={extensionError}
                disabled={isArchived}
              />
            )}
          </Field>
          <Field label="Description">
            {(id) => (
              <Textarea
                id={id}
                value={draft.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="Description used by AI to choose when and how to transfer the call."
                disabled={isArchived}
              />
            )}
          </Field>
          <Field label="Message to customer">
            {(id) => (
              <Textarea
                id={id}
                value={draft.message}
                onChange={(e) => setField('message', e.target.value)}
                placeholder="Enter message to be spoken to the customer before transfer. Leave empty to use the default transfer announcement."
                disabled={isArchived}
              />
            )}
          </Field>
          {hasPromptDefault(draft.handoffType) && (
            <Field label="Destination instructions">
              {(id) => (
                <Textarea
                  id={id}
                  value={draft.instructions}
                  onChange={(e) => setField('instructions', e.target.value)}
                  placeholder={
                    draft.handoffType === 'smart'
                      ? 'Prompt for the transfer assistant, including any IVR menu keypresses this destination needs (e.g. "press 2, wait, then press 4") \u2014 the dtmf tool is always available.'
                      : 'Instructions for the agent on how to handle the transfer.'
                  }
                  disabled={isArchived}
                />
              )}
            </Field>
          )}
          {draft.handoffType === 'smart' && (
            <>
              <Field label="First message mode">
                {(id) => (
                  <Select
                    value={draft.firstMessageMode}
                    onValueChange={(v) =>
                      setDraft((prev) => ({
                        ...prev,
                        firstMessageMode: v as SmartTransferFirstMessageMode,
                      }))
                    }
                    disabled={isArchived}>
                    <SelectTrigger id={id} className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIRST_MESSAGE_MODE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="First message">
                {(id) => (
                  <Input
                    id={id}
                    value={draft.firstMessage}
                    onChange={(e) => setField('firstMessage', e.target.value)}
                    placeholder="What the assistant says when the operator answers."
                    disabled={
                      isArchived ||
                      draft.firstMessageMode !== 'assistant-speaks-first'
                    }
                  />
                )}
              </Field>
              <Field label="Max duration (seconds)">
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    step={1}
                    min={SMART_MAX_DURATION_SECONDS_MIN}
                    max={SMART_MAX_DURATION_SECONDS_MAX}
                    value={maxDurationText}
                    onChange={(e) => {
                      const raw = e.target.value
                      setMaxDurationText(raw)
                      const parsed = Number(raw)
                      if (raw.trim() === '' || !Number.isFinite(parsed)) {
                        return
                      }
                      setNumberField(
                        'maxDurationSeconds',
                        clamp(
                          parsed,
                          SMART_MAX_DURATION_SECONDS_MIN,
                          SMART_MAX_DURATION_SECONDS_MAX
                        )
                      )
                    }}
                    onBlur={() =>
                      setMaxDurationText(String(draft.maxDurationSeconds))
                    }
                    disabled={isArchived}
                  />
                )}
              </Field>
              <Field label="Silence timeout (seconds)">
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    step={1}
                    min={SMART_SILENCE_TIMEOUT_SECONDS_MIN}
                    max={SMART_SILENCE_TIMEOUT_SECONDS_MAX}
                    value={silenceTimeoutText}
                    onChange={(e) => {
                      const raw = e.target.value
                      setSilenceTimeoutText(raw)
                      const parsed = Number(raw)
                      if (raw.trim() === '' || !Number.isFinite(parsed)) {
                        return
                      }
                      setNumberField(
                        'silenceTimeoutSeconds',
                        clamp(
                          parsed,
                          SMART_SILENCE_TIMEOUT_SECONDS_MIN,
                          SMART_SILENCE_TIMEOUT_SECONDS_MAX
                        )
                      )
                    }}
                    onBlur={() =>
                      setSilenceTimeoutText(String(draft.silenceTimeoutSeconds))
                    }
                    disabled={isArchived}
                  />
                )}
              </Field>
              <Field label="Fallback message">
                {(id) => (
                  <Textarea
                    id={id}
                    value={draft.fallbackMessage}
                    onChange={(e) =>
                      setField('fallbackMessage', e.target.value)
                    }
                    placeholder="Spoken to the customer if the transfer fails (destination unreachable, busy, or not human). Leave empty to use Vapi's default fallback behavior."
                    disabled={isArchived}
                  />
                )}
              </Field>
              <div className="flex items-center gap-2">
                <Checkbox
                  id={fallbackEndCallId}
                  checked={draft.fallbackEndCallEnabled}
                  onCheckedChange={(checked) =>
                    setDraft((prev) => ({
                      ...prev,
                      fallbackEndCallEnabled: checked === true,
                    }))
                  }
                  disabled={isArchived}
                />
                <Label htmlFor={fallbackEndCallId}>
                  End call after fallback message
                </Label>
              </div>
              <Field label="Context engineering">
                {(id) => (
                  <Select
                    value={draft.contextEngineering}
                    onValueChange={(v) =>
                      setDraft((prev) => ({
                        ...prev,
                        contextEngineering:
                          v as SmartTransferContextEngineering,
                      }))
                    }
                    disabled={isArchived}>
                    <SelectTrigger id={id} className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTEXT_ENGINEERING_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </>
          )}
        </div>
      </div>
      <div
        className={
          settingsOwner === 'organization'
            ? 'flex shrink-0 justify-end gap-2 p-4'
            : 'flex justify-end gap-2'
        }>
        <Button
          type="button"
          variant="outline"
          disabled={isSaving}
          onClick={onBack}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={isSaving || !isDirty}
          onClick={handleSave}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  )
}
